import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';

import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';

// Observable operators
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/distinctUntilChanged';

import {
  networkNotification,
  Notification,
  Scope,
  Scopes,
} from '../../entities/entities';

import {
  LoggingService,
  ScopeService
} from '../../services/services';
import { NotifyService } from '../../services/notify.service';

@Component({
  selector: 'app-scopes',
  templateUrl: './scopes.component.html',
  styleUrls: ['./scopes.component.css']
})
export class ScopesComponent implements OnInit {

  private searchTerms = new Subject<string>();

  @ViewChild('searchBox') searchBox: ElementRef;

  private currentScope: Scope; // The currenly selected scope. Useful for tracking current scope through multi-step operations like delete
  private currentScopeIndex = -1;
  scopes: Scopes;
  isError: boolean;
  isSuccess: boolean;
  isWorking: boolean;

  errorVerb: string;
  successVerb: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private loggingService: LoggingService,
    private notifyService: NotifyService,
    private scopeService: ScopeService
  ) {
    if (loggingService) {
      loggingService.logPageView('Scopes');
    }
  }

  private get searchText() { return this.searchBox.nativeElement.value; }
  private set searchText(value: string) { this.searchBox.nativeElement.value = value; }

  ngOnInit(): void {

    const searchParam = this.route.snapshot.queryParams['search'];
    if (searchParam) {
      this.searchText = searchParam;
    }

    this.searchScopes();

    this.searchTerms.distinctUntilChanged().debounceTime(300).subscribe(term => {
      if (term) {
        this.router.navigate( ['/scopes'],  { queryParams: { search: term } })
          .then(_ => this.searchScopes());
      } else {
        this.router.navigate( ['/scopes'])
          .then(_ => this.searchScopes());
      }
    });
  }

  private searchScopes() {
    this.isWorking = true;
    this.scopeService
      .search(this.searchText, true).subscribe(results => {
        this.scopes = results;
        this.isWorking = false;
      }, (error) => {
        this.isError = true;
        this.isWorking = false;
        this.notifyService.notifyError(error);
        if (this.loggingService) {
          this.loggingService.logError(error, 'reports.component.notifyFilterError', {
            'errorMessage': networkNotification.message
          });
        }
      });
  }

  getMoreScopes() {
    if (null != this.scopes) {
        if (this.scopes.token) {
            this.scopeService.search(this.searchText, true, this.scopes.token)
                .subscribe(results => {
                  const existingItems = this.scopes.items;
                  this.scopes.items = existingItems.concat(results.items);
                  this.scopes.token = results.token;
                });
        }
    }
}

  doSearch(term: string) {
    this.searchTerms.next(this.searchText);
  }

  setScopeForDelete(scope: Scope, currentScopeIndex: number): void {
    // store the currently selected scope
    this.currentScope = scope;
    this.currentScopeIndex = currentScopeIndex;
  }

  deleteScope(): void {
    this.isWorking = true;

    if (this.currentScope && this.currentScope.id && this.currentScopeIndex > -1) {
      this.scopeService.deleteScope(this.currentScope).subscribe(r => {
        // need to remove the item from the list
        this.scopes.items.splice(this.currentScopeIndex, 1);

        // reset our tracking variables
        this.currentScopeIndex = -1;
        this.currentScope = null;

        this.isSuccess = true;
        this.isError = false;
        this.isWorking = false;
        setTimeout(() => {
          this.isSuccess = false;
        }, 2000);
      }, err => {
        // reset our tracking variables
        this.currentScopeIndex = -1;
        this.currentScope = null;

        this.isSuccess = false;
        this.isError = true;
        this.isWorking = false;
        setTimeout(() => {
          this.isError = false;
        }, 2000);

      });
    } else {
      this.isWorking = false;
      // TODO: Error condition?
    }
  }

  gotoDetail(scope: Scope): void {
    // this.cdtRequestStateService.cdtRequest = cdtRequest;
    this.router.navigate(['/scopes', scope.id]);
  }

  newScope(): void {
    // this.cdtRequestStateService.cdtRequest = null;
    this.router.navigate(['/scopes', 'new']);
  }

  setOrderingVerbs(): void {
    this.errorVerb = 'moving';
    this.successVerb = 'moved';
  }

  setDeleteVerbs(): void {
    this.errorVerb = 'deleting';
    this.successVerb = 'deleted';
  }

  /**
   * Move the scope up or down in the display order. Currently we only support
   * moving one position at a time, but a drag and drop could also be implemented
   * without too much additional work (meaning we could move mutliple positions at a time).
   *
   * The move algorithm is based on using a single independent field to store the display order, vs
   * relying on a seconday index to store order.
   *
   * Implementation:
   * A new item is created with DisplayValue = (new Date).getTime() which returns the milliseconds
   * since the unix epoch in UTC.
   *
   * On the server, the values are sorted by displayvalue descending, so a new item always ends up on top, b/c
   * a new value will always have the highest number.
   *
   * When we move a value, we split the distance between the items we are moving the original item between.
   *
   * Ex: (values truncated to 5 digits for easier understanding.)
   * Item: DisplayOrder
   * A: 50000
   * B: 40000
   * c: 30000
   *
   * If we move C between A and B:
   * A: 50000
   * C: 45000
   * B: 40000
   *
   * If necessary we use decimal places, which would prevent collisions.:
   *
   * Ex:
   * Item: DisplayOrder
   * A: 50001
   * B: 50000
   * c: 30000
   *
   * If we move C between A and B:
   * A: 50001
   * C: 50000.5
   * B: 50000
   *
   * We don't follow this for the top element, b/c we'd end up with numbers higher than a new item:
   *
   * Ex:
   * Item: DisplayOrder
   * A: 50001
   * B: 50000
   *
   * If we move B before A (using Number.MAX_VALUE as our top limit, but any signifigantly large number would work):
   * B: 1.7976931348623157e+308
   * A: 50001
   *
   * Assume we add a new item with display order of 50002 (again truncated for ease of understanding), it would end up
   * item B.
   *
   * @param scope the scope to move
   * @param curIndex the current position
   * @param newIndex the new position
   * @param disabled If the move is disabled (top or bottom of list), done
   * here because can't disabled in UI without using actual button.
   */
  move(scope: Scope, curIndex: number, newIndex: number, disabled: boolean) {

    if (disabled) {
      return;
    }

    if (curIndex === newIndex) {
      // nothing to do
      return;
    }
    this.setOrderingVerbs();
    this.currentScope = scope;
    const targetScope = this.scopes.items[newIndex];

    let newDisplayOrder = 0;

    if (newIndex === 0) {
      // move item to top

      // this is a bit of an abitrary assignment, we need
      // it to be large enough to have room to move items
      // between these items, but not so big that a newly added
      // item would come after it.
      newDisplayOrder = targetScope.displayOrder + 1000;
    } else if (newIndex === this.scopes.items.length - 1) {
         // move item to the bottom
      if (this.scopes.token) {
        // this is an edge case, we can't move it to the bottom, because we don't have all
        // the scopes loaded.
        throw new Error('Can\'t move to the bottom unless all scopes are loaded.');
      } else {
        newDisplayOrder = targetScope.displayOrder - 1000;
      }
    } else {
      // otherwise move the item above or below
      let nextScopeIndex = 0;
      if (newIndex > curIndex) {
        // move down
        nextScopeIndex = newIndex + 1;
      } else if (newIndex < curIndex) {
        // move up
        nextScopeIndex = newIndex - 1;
      }
      // now try to compare
      if (nextScopeIndex >= 0 && nextScopeIndex < this.scopes.items.length) {
        const nextScope = this.scopes.items[nextScopeIndex];
        const absDistance = Math.abs(nextScope.displayOrder - targetScope.displayOrder);
        newDisplayOrder = (absDistance / 2.0) + Math.min(nextScope.displayOrder, targetScope.displayOrder);
      }

    }
    scope.displayOrder = newDisplayOrder;
    if (!scope.concurrencyToken) {
      scope.concurrencyToken = '"*"';
    }
    this.isWorking = true;
    this.scopeService.updateScope(scope).subscribe(rg => {

      // need to move the item in the list
      this.scopes.items[curIndex] = this.scopes.items[newIndex];
      this.scopes.items[newIndex] = rg;

      this.isError = false;
      this.isWorking = false;
      setTimeout(() => {
        this.isSuccess = false;
      }, 2000);
    }, error => {
      this.isSuccess = false;
      this.isError = true;
      this.isWorking = false;
      setTimeout(() => {
        this.isError = false;
      }, 2000);
    });
  }
}



// WEBPACK FOOTER //
// D:/a/1/s/src/app/components/scopes/scopes.component.ts